import { AI_HELP_RESERVED_SPACE_CSS_VAR } from '@constants/layout'
import { useCurrentUserLanguage } from '@localisation'
import { hasAcceptedAppMarketTos, useCurrentUser } from '@query'
import { GlobalStyle } from '@styles'
import theme from './styles/theme'
import {
  AnalyticEvents,
  bootFeaturebase,
  identifyFeaturebase,
  initFeaturebaseChangelogWidget,
  isFeaturebaseEnabled,
  identifyPostHogUser,
  sendUserDataToGtmAndPixel,
  setFeaturebaseLanguage,
  whenFeaturebaseBooted
} from '@utils'
import React, { useEffect, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import './App.css'
import * as Sentry from '@sentry/react'
import { AIHelpProvider, AppSplashProvider, MobileEmbedProvider, useMobileEmbed } from '@context'
import { useStoreReferral } from '@utils/storeReferral'
import { useStoreUTM } from '@utils/storeUTM'
import { LoginRedirect } from './pages/LoginRedirect'
import { useIsAuthenticated } from './context/AuthContext'
import { MOBILE_PATH_PREFIX } from './query/constants'
import BusinessBootstrapLayout from './pages/BusinessPage/BusinessBootstrapLayout'
import { SplashSignal } from '@components/SplashSignal'
import { GlobalSplashOverlay } from '@components/GlobalSplashOverlay'
import { fetchFeaturebaseJwt } from '@query/integrations/featurebase'

// Lazy load all page components
const BusinessesPage = React.lazy(() => import('@pages/BusinessesPage'))
const AgencyStartLayout = React.lazy(() => import('@pages/AgencyStart/AgencyStartLayout'))
const DemoLandingPage = React.lazy(() => import('@pages/DemoLandingPage'))
const NYLandingPage = React.lazy(() => import('@pages/NYLandingPage'))
const OnboardingPage = React.lazy(() => import('@pages/OnboardingPage'))
const StripeConfirmationPage = React.lazy(() => import('@pages/StripeConfirmationPage'))
const ErrorPage = React.lazy(() => import('@pages/ErrorPage'))
const TosPage = React.lazy(() => import('@pages/TosPage'))
const InvitePage = React.lazy(() => import('@pages/InvitePage'))
const MobileRouter = React.lazy(() => import('@pages/Mobile/MobileRouter'))

window.document.title = 'NoCFO'

const App: React.FC = () => {
  useStoreUTM()
  useStoreReferral()

  return (
    <>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <BrowserRouter>
          <MobileEmbedProvider>
            <ErrorBoundary FallbackComponent={ErrorPage}>
              <AIHelpProvider>
                <AppSplashProvider>
                  <GlobalSplashOverlay />
                  <Suspense fallback={<SplashSignal source="app-root-suspense" />}>
                    <Switch>
                      {/*
                       * The invite landing page is public. It is matched here, above
                       * `AppContent`, so that neither the login redirect nor the TOS gate
                       * can bounce an invited person away from their invitation.
                       */}
                      <Route path="/invite/:token" component={InvitePage} />
                      <Route render={() => <AppContent />} />
                    </Switch>
                  </Suspense>
                </AppSplashProvider>
              </AIHelpProvider>
            </ErrorBoundary>
          </MobileEmbedProvider>
        </BrowserRouter>

        <Toaster
          position="bottom-center"
          containerStyle={{
            right: `calc(16px + var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px))`
          }}
          toastOptions={{
            className: 'toast-message',
            success: {
              duration: 4000
            }
          }}
        />
      </ThemeProvider>
    </>
  )
}

const AppContent: React.FC = () => {
  const { isEmbedded, isRedirecting } = useMobileEmbed()
  const isAuthenticated = useIsAuthenticated()
  const { data: user } = useCurrentUser()
  const hasAcceptedTos = hasAcceptedAppMarketTos(user)
  const featurebaseOrganization = import.meta.env.VITE_FEATUREBASE_ORGANIZATION
  const featurebaseAppId = import.meta.env.VITE_FEATUREBASE_APP_ID

  useCurrentUserLanguage(user)

  useEffect(() => {
    let isCancelled = false

    // User accepted TOS
    // > Add user context to Sentry and Featurebase
    if (hasAcceptedTos && user) {
      Sentry.setUser({ id: user.id })

      void (async () => {
        try {
          if (!isFeaturebaseEnabled() || !featurebaseOrganization || !featurebaseAppId) return

          const { token } = await fetchFeaturebaseJwt()
          if (isCancelled) return

          bootFeaturebase({
            appId: featurebaseAppId,
            featurebaseJwt: token,
            language: user.language
          })
          identifyFeaturebase({
            organization: featurebaseOrganization,
            featurebaseJwt: token
          })

          if (user.language) {
            setFeaturebaseLanguage(user.language)
          }
        } catch {
          // Featurebase is non-blocking for app boot.
        }
      })()

      sendUserDataToGtmAndPixel(user, AnalyticEvents.LOGIN)
      identifyPostHogUser(user)
    }
    // User not accepted TOS
    // > Remove user from Sentry
    else {
      Sentry.setUser(null)
    }

    return () => {
      isCancelled = true
    }
  }, [featurebaseAppId, featurebaseOrganization, hasAcceptedTos, user])

  useEffect(() => {
    if (
      isEmbedded ||
      !hasAcceptedTos ||
      !isFeaturebaseEnabled() ||
      !featurebaseOrganization ||
      !featurebaseAppId
    ) {
      return
    }

    let isCancelled = false

    void whenFeaturebaseBooted().then(() => {
      if (!isCancelled) {
        initFeaturebaseChangelogWidget(featurebaseOrganization)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [featurebaseAppId, featurebaseOrganization, hasAcceptedTos, isEmbedded])

  if (isRedirecting) {
    return <SplashSignal source="app-mobile-redirecting" />
  }

  if (isAuthenticated === false) {
    return <LoginRedirect />
  }

  if (hasAcceptedTos === false) {
    return (
      <Suspense fallback={<SplashSignal source="tos-page-suspense" />}>
        <TosPage />
      </Suspense>
    )
  }

  if (isEmbedded) {
    return (
      <Suspense fallback={<SplashSignal source="embedded-mobile-router-suspense" />}>
        <Switch>
          <Route path={MOBILE_PATH_PREFIX} component={MobileRouter} />
        </Switch>
      </Suspense>
    )
  }

  return (
    <>
      <Switch>
        <Route exact path="/" component={RootRedirect} />
        <Route path="/start" component={AgencyStartLayout} />
        <Route path="/nuori-yrittaja" component={NYLandingPage} />
        <Route path="/demo" component={DemoLandingPage} />
        <Route path="/stripe-redirect" component={StripeConfirmationPage} />
        <Route path="/onboarding/:slug?" component={OnboardingPage} />

        {/* ACTUAL APPLICATION */}
        <Route path="/business/:rest?" component={LastBusinessRedirect} />
        <Route
          path="/:slug/"
          render={({ match: { url } }) => <BusinessBootstrapLayout baseUrl={url} />}
        ></Route>

        {/* LIST ALL THE BUSINESSES */}
        <Route path="/" component={BusinessesPage} />
      </Switch>
    </>
  )
}

const RootRedirect: React.FC = () => {
  const { data: user } = useCurrentUser()

  const shouldRedirectToOnboarding = Boolean(
    user && user.userships.length === 0 && user.invitations.length === 0
  )

  if (user) {
    return shouldRedirectToOnboarding ? <Redirect to="/onboarding" /> : <Redirect to="/start" />
  }

  return <SplashSignal source="root-redirect-user-loading" />
}

const LastBusinessRedirect: React.FC = () => {
  /**
   * Replace the `/business` path wildcard with the user's last active business slug.
   * Used by deeplinks (email, bank integrations, etc.) e.g. `/business/settings?page=luca`.
   */

  const { data: user } = useCurrentUser()
  const { pathname, search, hash } = useLocation()

  // Wait for user to load — without this we race and redirect to /start before the slug is known
  if (!user) {
    return <SplashSignal source="last-business-redirect-user-loading" />
  }

  if (!user.last_active_business_slug) return <Redirect to="/start" />

  const root = `/${user.last_active_business_slug}`
  const path = pathname.replace(/^\/business(?=\/|$)/, root) + search + hash

  return <Redirect to={path} />
}

export default App
